/**
 * geometry.ts — pure ring geometry, no DOM or Shopify dependencies.
 *
 * t ∈ [0,1) is the only coordinate stored for a placed charm.
 * (x,y) is always derived at render time — never stored.
 *
 * Convention: t=0 is top-center (closure anchor), increasing clockwise.
 */

export interface RingParams {
  /** Center x in canvas pixels */
  cx: number;
  /** Center y in canvas pixels */
  cy: number;
  /** Ring radius in canvas pixels */
  radius: number;
}

export interface XY {
  x: number;
  y: number;
}

export type CharmSize = 'XS' | 'S' | 'M' | 'L' | 'XL';

/** A charm placed on the ring: its position and its angular half-width (t-units),
 *  derived from the charm's rendered pixel size by the caller (canvas.ts). */
export interface PlacedCharm {
  t: number;
  hw: number;
}

// ─── Charm footprint (visual size source for canvas.ts) ──────────────────────

/**
 * Real charm footprint diameters in CENTIMETRES, from the filter size bands:
 * XS <1cm, S 1–1.5cm, M 1.5–2cm, L 2–4cm, XL >6cm. This drives the ON-SCREEN
 * pixel size (canvas.ts); the spacing half-width is then derived from that
 * rendered size, so reserved arc always matches the visible footprint.
 */
export const SIZE_CM: Record<CharmSize, number> = {
  XS: 1.2,   // bumped 0.8 → 1.2 (≈150%) so XS charms read at a usable size
  S:  1.25,
  M:  1.75,
  L:  3.0,
  XL: 6,
};

/**
 * The piece length (cm) the fixed ring visually represents. A charm covers
 * size_cm/REF_PIECE_CM of the loop (visual scale only — the chain length never
 * resizes the ring). Anchored to a ~20–30cm piece.
 */
export const REF_PIECE_CM = 30;

/**
 * Tiny safe gap between two charms' VISIBLE edges, in t-units. Half-widths now
 * track the rendered pixel size exactly, so this is the only slack and stays small.
 */
export const BREATHING_GAP = 0;

/**
 * Closure exclusion zone around t=0. No charm center may sit inside it; the
 * legal placement arc is [T_MIN, T_MAX]. Its two ends act as hard walls that
 * charms pack against but never cross. The zone (0.084 wide) is intentionally
 * wider than the largest possible minGap, so charms on opposite walls never
 * collide "across" the closure.
 */
export const T_MIN = 0.042;
export const T_MAX = 0.958;

/**
 * Clamp a t to the legal placement arc. Any t inside the closure zone snaps to
 * the nearest wall (T_MIN or T_MAX). Outside the zone, t is returned unchanged.
 */
export function clampToLegal(t: number): number {
  t = mod1(t);
  if (t >= T_MIN && t <= T_MAX) return t;
  return t < T_MIN ? T_MIN : T_MAX;
}

/**
 * Minimum valid center-to-center t distance between two charms: the sum of their
 * half-widths plus the breathing gap. Half-widths are in t-units, supplied by the
 * caller from each charm's rendered pixel size (see canvas.ts halfWidthOf).
 */
export function minGap(hwA: number, hwB: number): number {
  return hwA + hwB + BREATHING_GAP;
}

const EPS = 1e-9;

// ─── Core positioning ────────────────────────────────────────────────────────

/** The ring angle (radians) for a given t. t=0 → straight up (−90°). */
export function tToAngle(t: number): number {
  return -Math.PI / 2 + t * 2 * Math.PI;
}

/**
 * Map t ∈ [0,1) to a point ON the ring path.
 * t=0 → top-center, t=0.25 → right, t=0.5 → bottom, t=0.75 → left.
 */
export function tToXY(t: number, ring: RingParams): XY {
  const angle = tToAngle(t);
  return {
    x: ring.cx + ring.radius * Math.cos(angle),
    y: ring.cy + ring.radius * Math.sin(angle),
  };
}

/**
 * The charm's body center when it hangs from the ring at t.
 *
 * The charm's top-center anchor sits ON the ring at tToXY(t); the body is
 * pushed radially OUTWARD from the ring center by `offset` pixels (the charm's
 * extent). Because the offset is purely radial, this is just a point on a
 * concentric circle of radius `radius + offset` — charms splay outward all the
 * way around (below at the bottom, out to the sides, above at the top).
 *
 * Shared by the on-screen DOM layer and the offscreen PNG exporter.
 */
export function tToHangXY(t: number, ring: RingParams, offset: number): XY {
  const angle = tToAngle(t);
  const r = ring.radius + offset;
  return {
    x: ring.cx + r * Math.cos(angle),
    y: ring.cy + r * Math.sin(angle),
  };
}

/**
 * Resting rotation (radians) for a charm at t, so its top-center anchor points
 * toward the ring center and its body hangs radially outward. Upright (0) at the
 * bottom, ±90° at the sides, etc. This is the SAME radius angle used for the
 * positional offset, minus 90° (the body's default "down" → outward).
 *
 * The collision t is independent of this — only the visual orientation rotates.
 * Consumed by both the DOM layer and the PNG exporter.
 */
export function tToHangRotation(t: number): number {
  return tToAngle(t) - Math.PI / 2;
}

/**
 * Project an arbitrary screen point to its nearest t on the ring.
 * This is the snap: any drop position resolves to the ring arc.
 */
export function projectToRing(px: number, py: number, ring: RingParams): number {
  const dx = px - ring.cx;
  const dy = py - ring.cy;
  const angle = Math.atan2(dy, dx);
  return ((angle + Math.PI / 2) / (2 * Math.PI) + 1) % 1;
}

/** Euclidean distance from (px,py) to the ring arc (for the snap tolerance band). */
export function distToRing(px: number, py: number, ring: RingParams): number {
  const dx = px - ring.cx;
  const dy = py - ring.cy;
  return Math.abs(Math.sqrt(dx * dx + dy * dy) - ring.radius);
}

// ─── Spacing & placement ─────────────────────────────────────────────────────

/**
 * True when a charm of `size` placed at `t` clears every occupied charm by at
 * least their pairwise minGap. Uses modular distance (wraps at t=0/1).
 * Use as a commit-time guard.
 */
export function isSlotFree(
  t: number,
  hw: number,
  occupied: PlacedCharm[],
): boolean {
  if (t < T_MIN - EPS || t > T_MAX + EPS) return false; // inside closure zone
  return occupied.every(c => circularDist(t, c.t) >= minGap(hw, c.hw) - EPS);
}

/**
 * Resolve a dropped charm of `size` to the nearest valid t.
 *
 * If `desired` is already clear, it is returned unchanged. Otherwise the search
 * is anchored to the nearest blocking charm and walks outward on the side the
 * cursor came from first — placing the charm at *exactly* the minimum valid
 * distance from its neighbor (snug, only the breathing gap, never an overshoot).
 * If that whole side is full it falls back to the other side, then to `desired`
 * (caller enforces max-charm count before the ring is truly full).
 *
 * @param desired   - raw t from projectToRing
 * @param hw        - half-width (t-units) of the charm being placed
 * @param occupied  - charms already on the ring ({ t, hw })
 * @param excludeT  - optional t to exclude (the charm being repositioned)
 */
export function nudgeToFreeSlot(
  desired: number,
  hw: number,
  occupied: PlacedCharm[],
  excludeT: number | null = null,
): number {
  desired = clampToLegal(desired); // closure zone → nearest wall

  const others = excludeT === null
    ? occupied
    : occupied.filter(c => circularDist(c.t, mod1(excludeT)) > 1e-9);

  const gapTo    = (c: PlacedCharm) => minGap(hw, c.hw);
  const inBounds = (t: number) => t >= T_MIN - EPS && t <= T_MAX + EPS;
  const isFree   = (t: number) =>
    inBounds(t) && others.every(c => circularDist(t, c.t) >= gapTo(c) - EPS);

  if (isFree(desired)) return desired;
  if (others.length === 0) return desired; // already clamped legal, nothing to dodge

  // Nearest blocking charm — the anchor we want to sit snug against.
  let blocker = others[0];
  let bestDist = circularDist(desired, others[0].t);
  for (const c of others) {
    const d = circularDist(desired, c.t);
    if (d < bestDist) { bestDist = d; blocker = c; }
  }

  // Which side of the blocker did the cursor land on?
  // CW-arc from blocker to desired = mod1(desired - blocker.t); < 0.5 ⇒ CW.
  const desiredIsCW = mod1(desired - blocker.t) < 0.5;

  // Forward arc distance from `from` to `to` travelling in `dir` (+1 CW / −1 CCW).
  const forward = (from: number, to: number, dir: 1 | -1) =>
    dir === 1 ? mod1(to - from) : mod1(from - to);

  const sweep = (dir: 1 | -1): number | null => {
    // Start snug against the blocker on this side. (No mod1: the legal arc
    // doesn't wrap — the closure walls break the ring's continuity — so a
    // position past a wall must read as out-of-bounds, not wrap around.)
    let pos = blocker.t + dir * gapTo(blocker);
    for (let guard = 0; guard <= others.length + 1; guard++) {
      if (!inBounds(pos)) return null; // packed up against the closure wall
      // Of the charms still conflicting at `pos`, advance past whichever pushes
      // us furthest forward — clears a dense cluster without overshooting.
      let next: number | null = null;
      let maxFwd = 0;
      for (const c of others) {
        if (circularDist(pos, c.t) < gapTo(c) - EPS) {
          const cand = c.t + dir * gapTo(c);
          const fwd  = forward(pos, cand, dir);
          if (fwd > maxFwd) { maxFwd = fwd; next = cand; }
        }
      }
      if (next === null) return pos; // no conflicts, in bounds → snug & valid
      pos = next;
    }
    return null; // this direction is full
  };

  const primary:   1 | -1 = desiredIsCW ? 1 : -1;
  const secondary: 1 | -1 = desiredIsCW ? -1 : 1;

  return sweep(primary) ?? sweep(secondary) ?? desired;
}

// ─── Internal helpers ──────────────────────────────────────────────────────

/** Shortest arc distance between two t-values (always ≤ 0.5). */
export function circularDist(a: number, b: number): number {
  const d = Math.abs(a - b) % 1;
  return d > 0.5 ? 1 - d : d;
}

/** Normalize any number to [0,1). */
function mod1(t: number): number {
  return ((t % 1) + 1) % 1;
}
